import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

import { getRuleValue, useAccessRule } from '../../../hooks/auth/use-access-rule';
import { useFeatureToggle } from '../../../hooks/use-feature-toggle';
import { useTranslations } from '../../../hooks/use-translations';
import {
  AccountDetailsIcon,
  AllReportIcon,
  DashboardIcon,
  RejectedTransactionsIcon,
  TransactionManagementIcon,
  PaymentGateIcon,
  WalletOutlinedIcon,
} from '../../icons';

import { MenuListItems } from './Sidebar';

export const useNavMenu = (): MenuListItems => {
  const t = useTranslations('sidebar.navMenu');
  const theme = useTheme();
  const { featureToggle } = useFeatureToggle();
  const rule = useAccessRule();

  const withdrawalsEnabled = useMemo(() => {
    return getRuleValue(rule, 'withdrawals.payoffs');
  }, [rule]);

  const color = theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[500];

  const result: MenuListItems = [];

  if (!featureToggle['page.dashboard.hidden']) {
    result.push({
      title: t('Dashboard'),
      target: '/',
      icon: <DashboardIcon color={color} />,
    });
  }

  if (!featureToggle['page.account-details.hidden']) {
    result.push({
      title: t('AccountDetails'),
      target: '/account-details',
      icon: <AccountDetailsIcon color={color} />,
      demo: true,
    });
  }

  if (!featureToggle['page.transaction-management.hidden']) {
    result.push({
      title: t('TransactionManagement'),
      target: '/transaction-management',
      icon: <TransactionManagementIcon color={color} />,
    });
  }

  if (!featureToggle['page.rejected-transaction.hidden']) {
    result.push({
      title: t('RejectedTransaction'),
      target: '/rejected-transaction',
      icon: <RejectedTransactionsIcon color={color} />,
    });
  }

  if (!featureToggle['page.virtual-terminal.hidden']) {
    result.push({
      title: t('PaymentGates'),
      target: '/payment-gates',
      icon: <PaymentGateIcon color={color} />,
      demo: false,
    });
  }

  if (!featureToggle['page.all-reports.hidden']) {
    result.push({
      title: t('AllReports'),
      target: '/reports/all',
      icon: <AllReportIcon color={color} />,
    });
  }

  if (!featureToggle['page.withdrawals.hidden']) {
    result.push({
      title: t('Withdrawals'),
      target: withdrawalsEnabled ? '/withdrawals' : '/withdrawals/payout-table',
      icon: <WalletOutlinedIcon />,
    });
  }

  return result;
};
